"use client"

import styled from "@emotion/styled"
import { Box, Button } from "@mui/material"
import { Image } from "next/dist/client/image-component"
import { useEffect, useRef, useState } from "react"
import { Mousewheel, Scrollbar } from "swiper/modules"
import { Swiper, SwiperSlide } from "swiper/react"
import { Desktop, Mobile, NewText } from "ui"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledSliderWrapper = styled.div`
  position: relative;
  width: 100vw;
  height: 100dvh;
  .customSliderSlide {
  }
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 100%;
  }
`
const StyledImageWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`

const StyledTextWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  min-height: 453px;
  max-height: 100%;
  background: linear-gradient(180deg, rgb(0 0 0 / 80%) 0%, transparent 90.55%);
  padding: 71px 63px;
  width: 100%;
  z-index: 9;
  p {
    margin: 0;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 71px 120px;
    min-height: 100%;
  }
`
const StyledBottomText = styled(NewText)`
  position: absolute;
  bottom: 15px;
  left: 0;
  padding: 3px 28px 1px;
  background-color: rgba(0, 0, 0, 0.43);
  ${(props) => props.theme.breakpoints.up("xl")} {
    left: 120px;
    padding: 3px 15px 1px 28px;
  }
`

const StyledBottomTextWrapper = styled.div`
  padding: 23px 31px 16px 16px;
  p {
    margin: 0;
  }
`

const StyledButton = styled(Button)`
  font-size: 18.79px;
  text-decoration: unset;
  width: fit-content;
  height: 32px;
  padding: 0px 18px;
  background-color: #000000;
  border: unset;
  color: #ffffff;
  border-radius: 12.65px;
  letter-spacing: -0.01em;
  :hover {
    background-color: #000000;
  }
`

type Props = {
  locale: string
  data: any
  companyId?: number
  companyName?: string
  messages?: any
  scrollDownToForm: () => void
}

export const LandingSlider: React.FC<Props> = ({ data, scrollDownToForm }: Props) => {
  const imageSlider: {
    imageForDesktop: string
    imageForMobile: string
    title: string
    description: string
    imageCredit: string
    backgroundColorForMobile: string
  }[] = extractProperty(data, "imageSkuder")?.map((image: any) => {
    const imageForDesktop = extractProperty(image, "imageForDesktop")
    const imageForMobile = extractProperty(image, "imageForMobile")
    const title = extractProperty(image, "title")
    const description = extractProperty(image, "description")
    const imageCredit = extractProperty(image, "imageCredit")
    const backgroundColorForMobile = extractProperty(image, "backgroundColorForMobile")

    return {
      imageForDesktop,
      imageForMobile,
      title,
      description,
      imageCredit,
      backgroundColorForMobile,
    }
  })
  const formButton = extractProperty(data, "formButton")?.[0]?.name

  const bottomRefs = useRef<(HTMLDivElement | null)[]>([])
  const [bottomHeights, setBottomHeights] = useState<number[]>([])

  useEffect(() => {
    if (!bottomRefs.current) return

    const newHeights = bottomRefs.current.map((ref) => ref?.offsetHeight || 0)
    setBottomHeights(newHeights)
  }, [imageSlider.length])

  return (
    <StyledSliderWrapper>
      <Swiper
        style={{
          padding: 0,
          height: "100%",
          width: "100%",
        }}
        direction="vertical"
        spaceBetween={0}
        modules={[Scrollbar, Mousewheel]}
        scrollbar={{ draggable: true }}
        mousewheel={{ releaseOnEdges: true }}
        touchReleaseOnEdges={true}
      >
        {imageSlider.map(
          (
            item: {
              imageForDesktop: string
              imageForMobile: string
              title: string
              description: string
              imageCredit: string
              backgroundColorForMobile: string
            },
            key: number,
          ) => (
            <SwiperSlide
              key={key.toString()}
              style={{
                width: "100%",
              }}
            >
              <Desktop sx={{ height: "100%" }}>
                <StyledImageWrapper>
                  {(item.title || item.description) && (
                    <StyledTextWrapper>
                      <NewText
                        component={"h2"}
                        desktopFontSize={48.14}
                        desktopFontWeight={700}
                        desktopLineHeight={"38.7px"}
                        letterSpacing={"-0.01em"}
                        color="white"
                        maxWidth={"613px"}
                      >
                        {item.title}
                      </NewText>
                      <NewText
                        component={"p"}
                        desktopFontSize={48.14}
                        desktopFontWeight={500}
                        desktopLineHeight={"46.7px"}
                        letterSpacing={"-0.02em"}
                        color="white"
                        dangerouslySetInnerHTML={{
                          __html: item.description,
                        }}
                        maxWidth={"613px"}
                      />
                    </StyledTextWrapper>
                  )}
                  <Image
                    src={getUmbracoMediaUrl(item?.imageForDesktop)}
                    fill
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                      objectPosition: "top",
                    }}
                    alt={item.imageCredit}
                    priority={key === 0}
                  />
                  <StyledBottomText
                    component={"span"}
                    desktopFontSize={14}
                    desktopLineHeight={"13px"}
                    desktopFontWeight={600}
                    color="white"
                    padding={"0 28px"}
                  >
                    הדמיות להמחשה בלבד. טל.ח.
                  </StyledBottomText>
                </StyledImageWrapper>
              </Desktop>
              <Mobile sx={{ height: "100dvh" }}>
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <Box
                    sx={{
                      height: `calc(100% - ${bottomHeights[key] || 0}px)`,
                      transition: "height 0.3s ease",
                      position: "relative",
                    }}
                  >
                    <Image
                      src={getUmbracoMediaUrl(item?.imageForMobile)}
                      fill
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top",
                      }}
                      alt={item.imageCredit}
                      priority={key === 0}
                    />
                  </Box>
                  <StyledBottomTextWrapper
                    ref={(el) => (bottomRefs.current[key] = el)}
                    style={{ backgroundColor: `#${item.backgroundColorForMobile}` }}
                  >
                    {(item.title || item.description) && (
                      <Box marginBottom={"16px"} maxWidth={"273px"}>
                        <NewText
                          component={"h2"}
                          desktopFontSize={25.79}
                          desktopFontWeight={700}
                          desktopLineHeight={"22.2px"}
                          letterSpacing={"-0.05em"}
                          color="white"
                        >
                          {item.title}
                        </NewText>
                        <NewText
                          component={"p"}
                          desktopFontSize={25.79}
                          desktopFontWeight={500}
                          desktopLineHeight={"22.2px"}
                          letterSpacing={"-0.05em"}
                          color="white"
                          dangerouslySetInnerHTML={{
                            __html: item.description,
                          }}
                          marginBottom={"16px"}
                        />
                      </Box>
                    )}
                    <StyledButton onClick={scrollDownToForm}>{formButton}</StyledButton>
                  </StyledBottomTextWrapper>
                </Box>
              </Mobile>
            </SwiperSlide>
          ),
        )}
      </Swiper>
    </StyledSliderWrapper>
  )
}

export default LandingSlider
